import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, TextField, Typography, Paper, Alert, InputAdornment, Skeleton, Divider, Chip, Button } from "@mui/material";
import DatePicker from "@/components/date-picker";
import { useCalculateDutiesQuery, DutyCalculationInput, LineType } from "@/services/product-catalogue";
import CreateProductForm from "../create-product-form";
import { useGetCountrySummaryQuery, useGetHTSCodesQuery } from "@/services/dashboard/sm-api";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { CountrySelect, HTSCodeSelect } from "../product-select-fields";
import { useLazyCheckMetalPercentageRequiredQuery } from "@/services/product-catalogue/metal-percentage-api";
import { Factory as FactoryIcon, Star as StarIcon } from "@mui/icons-material";
import { getAuthorityStyle } from "../rule-form-section/helpers";
import { useGetDutyColumnsQuery, DutyColumn } from "@/services/product-catalogue/duty-columns-api";

const DutyCalculator: React.FC = () => {
  const { user } = useAppSelector((state: any) => state.auth);
  const { selectedClient } = useAppSelector((state: any) => state.client);
  const { selectedTenant } = useAppSelector((state: any) => state.tenantManagement);

  const [htsCode, setHtsCode] = useState("");
  const [countryOfOrigin, setCountryOfOrigin] = useState("");
  const [metalPercentage, setMetalPercentage] = useState<string>("");
  const [showMetalPercentage, setShowMetalPercentage] = useState(false);
  const [_, setMetalRules] = useState<any[]>([]);
  const [effectiveDate, setEffectiveDate] = useState<string>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  });

  // Debounced input values for live compute
  const [debouncedHtsCode, setDebouncedHtsCode] = useState("");
  const [debouncedCountryOfOrigin, setDebouncedCountryOfOrigin] = useState("");
  const [debouncedMetalPercentage, setDebouncedMetalPercentage] = useState<string>("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedHtsCode(htsCode), 400);
    return () => clearTimeout(t);
  }, [htsCode]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCountryOfOrigin(countryOfOrigin), 400);
    return () => clearTimeout(t);
  }, [countryOfOrigin]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedMetalPercentage(metalPercentage), 400);
    return () => clearTimeout(t);
  }, [metalPercentage]);

  // Autofill metal percentage to 100% when metal is required and user hasn't entered a value
  useEffect(() => {
    if (showMetalPercentage && (!metalPercentage || metalPercentage.trim() === "")) {
      setMetalPercentage("100");
    }
  }, [showMetalPercentage]);

  const input: DutyCalculationInput | null = useMemo(() => {
    if (!debouncedHtsCode || !debouncedCountryOfOrigin) return null;
    return {
      htsCode: debouncedHtsCode,
      countryOfOrigin: debouncedCountryOfOrigin,
      effectiveDate,
      metalPercentage: debouncedMetalPercentage ? Number(debouncedMetalPercentage) : undefined,
    };
  }, [debouncedHtsCode, debouncedCountryOfOrigin, effectiveDate, debouncedMetalPercentage]);

  const { data, isFetching } = useCalculateDutiesQuery(input!, { skip: !input });

  const totalRatePct = useMemo(() => ((data?.totalDutyRate ?? 0) * 100).toFixed(2), [data]);

  // Value form state
  const [goodsValue, setGoodsValue] = useState<string>("");

  // Create Product modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const matchedLabel = useMemo(() => {
    if (!data?.htsMatchedCode) return null;
    const level = data?.htsMatchLevel ? `${data.htsMatchLevel}-digit` : 'code';
    return `Matched at ${level}: ${data.htsMatchedCode}`;
  }, [data]);

  const parsedGoodsValue = useMemo(() => {
    const n = Number(goodsValue.replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  }, [goodsValue]);

  const estimatedDutyAmount = useMemo(() => {
    const rate = data?.totalDutyRate ?? 0;
    return parsedGoodsValue * rate;
  }, [parsedGoodsValue, data]);

  // client selection logic mirrors product forms
  const isTDAdminTCD = useMemo(() => user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT", [user, selectedTenant]);
  const clientId = useMemo(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) return user?._id;
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  // countries
  const { data: countrySummary, isLoading: isLoadingCountries } = useGetCountrySummaryQuery(
    { clientId: clientId || "" },
    { skip: !clientId }
  );
  const allCountryCodes = useMemo(() => countrySummary?.getCountryOriginSummary?.map((c: any) => c.country_code) || [], [countrySummary]);

  // hts options filtered by country (or all)
  const { data: htsCodes, isLoading: isLoadingHTS } = useGetHTSCodesQuery(
    { clientId: clientId || "", country_codes: countryOfOrigin ? [countryOfOrigin] : allCountryCodes },
    { skip: !clientId || allCountryCodes.length === 0 }
  );

  // metal percentage dynamic check
  const [checkMetalPercentage] = useLazyCheckMetalPercentageRequiredQuery();
  useMemo(() => {
    const timer = setTimeout(async () => {
      if (htsCode && countryOfOrigin) {
        try {
          const result = await checkMetalPercentage({ htsCode: htsCode.replace(/\./g, ''), countryOfOrigin }).unwrap();
          setShowMetalPercentage(result.isMetalPercentageRequired);
          setMetalRules(result.applicableMetalRules || []);
        } catch {
          setShowMetalPercentage(false);
          setMetalRules([]);
        }
      } else {
        setShowMetalPercentage(false);
        setMetalRules([]);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [htsCode, countryOfOrigin, checkMetalPercentage]);

  // When metal is not required anymore, clear any metal percentage to avoid ghost effects
  useEffect(() => {
    if (!showMetalPercentage && metalPercentage) {
      setMetalPercentage("");
    }
  }, [showMetalPercentage]);
  
  // Fetch duty columns to build citations footnotes
  const { data: dutyColumnsResp } = useGetDutyColumnsQuery({ pageNo: 1, pageSize: 500 } as any);
  const dutyColumns: DutyColumn[] = dutyColumnsResp?.data || [];
  const columnMap = useMemo(() => {
    const m = new Map<string, DutyColumn>();
    dutyColumns.forEach(dc => dc && dc._id && m.set(dc._id, dc));
    return m;
  }, [dutyColumns]);
  const footnoteColumns = useMemo(() => {
    const ids = new Set<string>();
    (data?.breakdownLines || []).forEach(line => {
      (line.calculationBreakdown || []).forEach(calc => {
        if (calc.applied && typeof calc.columnId === 'string' && calc.columnId !== 'general-duty' && calc.columnId !== 'secondary-duty') {
          ids.add(calc.columnId as string);
        }
      });
    });
    return Array.from(ids)
      .map(id => columnMap.get(id))
      .filter((dc): dc is DutyColumn => !!dc && Array.isArray((dc as any).citations) && (dc as any).citations.length > 0);
  }, [data, columnMap]);

  return (
    <Stack spacing={3} sx={{ minHeight: '70vh' }}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e5e7eb', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <CountrySelect
            value={countryOfOrigin}
            onChange={setCountryOfOrigin}
            countries={countrySummary?.getCountryOriginSummary || []}
            isLoading={isLoadingCountries}
            required
          />
          <HTSCodeSelect
            value={htsCode}
            onChange={setHtsCode}
            htsCodes={htsCodes?.getHTSCodesByCountry || []}
            isLoading={isLoadingHTS}
            required
          />
          {showMetalPercentage && (
            <Box sx={{ minWidth: 220 }}>
              <TextField
                label="Metal Percentage (%)"
                type="number"
                value={metalPercentage}
                onChange={(e) => setMetalPercentage(e.target.value)}
                placeholder="e.g., 85.5"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  inputProps: { min: 0, max: 100, step: 0.1 },
                  startAdornment: <FactoryIcon sx={{ color: '#f59e0b', mr: 1, fontSize: 20 }} />,
                }}
                helperText="Optional. Adjusts Section 232 duty by metal content."
              />
            </Box>
          )}
          <DatePicker value={effectiveDate} onChangeValue={(d) => setEffectiveDate(d ?? effectiveDate)} sx={{ minWidth: 180 }} />
          <Box sx={{ ml: { md: 'auto' } }}>
            <Button
              variant="contained"
              onClick={() => setIsCreateOpen(true)}
              disabled={!data || !debouncedHtsCode || !debouncedCountryOfOrigin}
              sx={{
                background: "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), #0B494B",
                border: "1px solid #FFFFFF",
                boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
                borderRadius: "8px",
                color: "#FFFFFF",
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                padding: '6px 12px',
                whiteSpace: 'nowrap',
                '&:hover': {
                   background: "#093b3d",
                }
              }}
            >
              Create Product
            </Button>
          </Box>
        </Stack>
      </Paper>

      {!isFetching && !data && (
        <Alert severity="warning">Enter HTS code and origin to calculate duties.</Alert>
      )}

      {data && (
        <Stack spacing={2}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e5e7eb', backgroundColor: '#f9fffb' }}>
            {isFetching ? (
              <Skeleton variant="text" width={240} height={28} />
            ) : (
              <Stack spacing={0.5}>
                <Typography variant="subtitle1" fontWeight={800} color="#052223">
                  Total ad valorem rate: {totalRatePct}%
                </Typography>
                {(matchedLabel || data?.htsDescription) && (
                  <Typography variant="caption" color="text.secondary">
                    {matchedLabel}{matchedLabel && data?.htsDescription ? ' — ' : ''}{data?.htsDescription}
                  </Typography>
                )}
                {data?.secondaryDutyRateString && data.secondaryDutyRateString.toString().trim().toLowerCase() !== 'null' && (
                  <Typography variant="caption" color="text.secondary">
                    Specific Duty (HTS): {data.secondaryDutyRateString}
                  </Typography>
                )}
                {data?.ftaApplicable ? (
                  <Alert severity="success" sx={{ mt: 0.5, py: 0.5 }}>
                    <Typography variant="body2">
                      Preferential origin applies{data?.ftaProgramName ? ` under ${data.ftaProgramName}` : ''}. General duty may be exempt.
                    </Typography>
                  </Alert>
                ) : null}
              </Stack>
            )}
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #e5e7eb', backgroundColor: '#ffffff' }}>
            {isFetching ? (
              <Stack spacing={1}>
                <Skeleton variant="rectangular" height={56} />
                <Skeleton variant="rectangular" height={56} />
              </Stack>
            ) : (
              <>
                <Typography variant="subtitle2" fontWeight={800} color="#0B494B" sx={{ mb: 1 }}>Value</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Goods Value"
                    value={goodsValue}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const cleaned = raw.replace(/[^0-9.]/g, '');
                      setGoodsValue(cleaned);
                    }}
                    placeholder="10,000.00"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputMode: 'decimal',
                    }}
                    helperText="Estimates based on current column rules; confirm with a broker."
                    sx={{ minWidth: 240 }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label="$1k" onClick={() => setGoodsValue("1000")}/>
                    <Chip label="$10k" onClick={() => setGoodsValue("10000")}/>
                    <Chip label="$100k" onClick={() => setGoodsValue("100000")}/>
                  </Stack>
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary">Estimated Duty</Typography>
                    <Typography variant="h6" fontWeight={900}>${estimatedDutyAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                  </Box>
                </Stack>
                
              </>
            )}
          </Paper>

          {/* Removed duplicate applied programs list above metal/non-metal sections */}

          {isFetching
            ? Array.from({ length: Math.max(1, (data.breakdownLines?.length || 2)) }).map((_, idx) => (
                <Paper key={`s-${idx}`} elevation={0} sx={{ p: 2.5, backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  <Skeleton variant="text" width="30%" height={24} />
                  <Skeleton variant="text" width="20%" />
                  <Divider sx={{ my: 1.5 }} />
                  <Skeleton variant="text" width="15%" />
                  <Skeleton variant="text" width="50%" />
                  <Skeleton variant="text" width="40%" />
                </Paper>
              ))
            : data.breakdownLines.map((line: any, idx: number) => (
                <Paper key={idx} elevation={0} sx={{ border: "1px solid #F3F4F6", borderRadius: "8px", p: 1.5, bgcolor: '#FAFAFA' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={line.lineType === LineType.METAL ? 'Metal' : line.lineType === LineType.NONMETAL ? 'Non-Metal' : 'Full Value'}
                        size="small"
                        sx={{
                          backgroundColor: line.lineType === LineType.METAL ? '#FFF7ED' : line.lineType === LineType.NONMETAL ? '#EFF6FF' : '#ECFDF5',
                          color: line.lineType === LineType.METAL ? '#C2410C' : line.lineType === LineType.NONMETAL ? '#1D4ED8' : '#047857',
                          fontWeight: 600,
                          borderRadius: '4px',
                          border: '1px solid transparent',
                          borderColor: line.lineType === LineType.METAL ? '#FFEDD5' : line.lineType === LineType.NONMETAL ? '#DBEAFE' : '#D1FAE5',
                        }}
                      />
                      {line.portion < 1 && (
                        <Typography variant="caption" color="text.secondary" fontFamily="Inter, sans-serif">({(line.portion * 100).toFixed(0)}% of value)</Typography>
                      )}
                    {line.exclusionsApplied && line.exclusionsApplied.length > 0 && (
                      <StarIcon sx={{ fontSize: 16, color: getAuthorityStyle(line.exclusionsApplied[0]?.authority).color }} />
                    )}
                    </Stack>
                    <Typography variant="body2" fontWeight={600} fontFamily="Inter, sans-serif">{(line.totalRate * 100).toFixed(2)}%</Typography>
                  </Stack>
                  <Divider sx={{ my: 1, borderColor: '#E5E7EB' }} />
                  {line.applicableRules.length > 0 ? (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 0.5, display: 'block' }} fontFamily="Inter, sans-serif">APPLIED RULES</Typography>
                      <Box component="ul" sx={{ pl: 2.5, my: 0 }}>
                      {line.applicableRules.map((rule: any, ridx: number) => {
                        const excluded = (line.exclusionsApplied || []).some((e: any) => e.columnId === rule.columnId);
                        const color = excluded ? getAuthorityStyle((line.exclusionsApplied || []).find((e: any) => e.columnId === rule.columnId)?.authority).color : undefined;
                        // Render specific duty string instead of percent for HTS secondary component
                        if (rule.columnId === 'secondary-duty') {
                          const s = (data?.secondaryDutyRateString || '').toString().trim();
                          if (!s || s.toLowerCase() === 'null') {
                            return null;
                          }
                          return (
                            <li key={ridx}>
                              <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">
                                Specific Duty: {s}
                              </Typography>
                            </li>
                          );
                        }
                        return (
                          <li key={ridx}>
                            <Typography variant="body2" fontSize={13} fontFamily="Inter, sans-serif">
                              {rule.columnName}: {(rule.rate * 100).toFixed(2)}%
                              {excluded && (
                                <>
                                  <StarIcon sx={{ fontSize: 14, color, verticalAlign: 'middle', ml: 0.5 }} />
                                  <span style={{ marginLeft: 4, color: '#059669', fontWeight: 500 }}>{"→ 0%"}</span>
                                </>
                              )}
                            </Typography>
                          </li>
                        );
                      })}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic" fontFamily="Inter, sans-serif">No applicable duties on this portion.</Typography>
                  )}
                </Paper>
              ))}
          
          {/* Exclusions legend */}
          {data.breakdownLines.some((line: any) => (line.exclusionsApplied || []).length > 0) && (
            <Paper elevation={0} sx={{ p: 2.5, backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Exclusions</Typography>
              <Stack spacing={1}>
                {Array.from(
                  new Map(
                    data.breakdownLines.flatMap((l: any) => (l.exclusionsApplied || [])).map((e: any) => [e.listId + e.columnId, e])
                  ).values()
                ).map((e: any, idx: number) => {
                  const color = getAuthorityStyle(e.authority).color;
                  return (
                    <Stack key={`${e.listId}-${e.columnId}-${idx}`} direction="row" spacing={1} alignItems="center">
                      <StarIcon sx={{ fontSize: 16, color }} />
                      <Typography variant="body2">
                        {e.listName}: {e.columnName} — {e.matchedCode}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          )}
          
          {/* Footnotes for citations */}
          {footnoteColumns.length > 0 && (
            <Paper elevation={0} sx={{ p: 1.5, border: "1px solid #E5E7EB", borderRadius: '8px', bgcolor: '#F9FAFB' }}>
              <Typography variant="subtitle2" fontSize={12} fontWeight={700} sx={{ mb: 1, textTransform: 'uppercase', color: '#4B5563' }}>Sources</Typography>
              <Stack spacing={1.5}>
                {footnoteColumns.map((dc: any, idx: number) => (
                  <Box key={dc._id}>
                    <Typography variant="body2" fontWeight={600} fontSize={13} color="#111827">
                      {idx + 1}. {dc.name}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
                      {(dc.citations || []).map((c: any, cidx: number) => {
                        return (
                          <li key={cidx}>
                            <Typography variant="body2" fontSize={13} sx={{ wordBreak: 'break-all' }}>
                              <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ color: '#0B494B', textDecoration: 'none' }}>{c.url}</a>
                            </Typography>
                          </li>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

        </Stack>
      )}

      {/* Create Product Modal - reuse existing component */}
      <CreateProductForm
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onProductCreated={() => {
          setIsCreateOpen(false);
          window.dispatchEvent(new CustomEvent('td:navigate-products'));
        }}
        initialData={{
          htsCode: debouncedHtsCode,
          countryOfOrigin: debouncedCountryOfOrigin,
          metalPercentage: showMetalPercentage && metalPercentage ? Number(metalPercentage) : undefined,
        }}
      />
    </Stack>
  );
};

export default DutyCalculator;


